import { Button, Checkbox, Table, TableActionBar, TableActionBarOverlay, TableCell, TableRow } from '@amzn/meridian';
import {
  type PapiSearchFieldPapiUserFragment,
  TrafficProjectPermissionType,
  type UsersPermissionTableUserPermissionFragment,
  type UsersTableUserFragment,
  type UserTableProjectDataFragment,
} from 'generated/graphql';
import { type FC, useCallback, useState } from 'react';
import { setAllKeys } from '../TPShareModalUtil';
import { PAPISearchField } from './PAPISearchField/PAPISearchField';
import { UsersPermissionTableRow } from './UsersPermissionTableRow/UsersPermissionsTableRow';

export type UsersPermissionTableProps = {
  userPermissions: UsersPermissionTableUserPermissionFragment[];
  projectData: UserTableProjectDataFragment;
  userData: UsersTableUserFragment;
  setUserPermissions: (updatedPermissions: UsersPermissionTableUserPermissionFragment[]) => void;
};

export const UsersPermissionTable: FC<UsersPermissionTableProps> = ({ userPermissions, setUserPermissions, projectData, userData }) => {
  const isRowRemovable = (
    permissionData: UsersPermissionTableUserPermissionFragment,
    userData: UsersTableUserFragment,
    projectData: UserTableProjectDataFragment,
  ): boolean => permissionData.user.alias !== userData.alias && permissionData.user.alias !== projectData.createdBy;

  const removableRows = userPermissions.filter((permission) => isRowRemovable(permission, userData, projectData));

  const [selectedRows, setSelectedRows] = useState<{
    [alias: UsersTableUserFragment['alias']]: boolean;
  }>(
    removableRows.reduce(
      (selected, permissionOb) => {
        selected[permissionOb.user.alias] = false;
        return selected;
      },
      {} as {
        [alias: UsersTableUserFragment['alias']]: boolean;
      },
    ),
  );

  const numRows = Object.keys(selectedRows).length;
  const numCheckedRows = Object.values(selectedRows).filter(Boolean).length;
  const allSelected = numRows === numCheckedRows;

  const onChangeRow = useCallback(
    (alias: string) => (value: boolean) =>
      setSelectedRows({
        ...selectedRows,
        [alias]: value,
      }),
    [
      selectedRows,
    ],
  );

  const getSelectedAliases = useCallback(
    () =>
      Object.entries(selectedRows)
        .filter(([, value]) => value === true)
        .map(([key]) => key),
    [
      selectedRows,
    ],
  );

  const removeUsersFromPermissionsByAlias = useCallback(
    (aliases: string[]) => {
      // Remove the users from the set.
      setUserPermissions(
        [
          ...userPermissions,
        ].filter((obj) => !aliases.includes(obj.user.alias)),
      );
      // Update checkbox selection
      setSelectedRows((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => !aliases.includes(key))));
    },
    [
      setUserPermissions,
      userPermissions,
    ],
  );

  const addUserToPermissions = useCallback(
    (userObj: UsersPermissionTableUserPermissionFragment) => {
      setUserPermissions([
        ...userPermissions,
        userObj,
      ]);
    },
    [
      setUserPermissions,
      userPermissions,
    ],
  );

  const onChangeHeader = () => {
    const allValues = !allSelected;
    const nextSelected = {
      ...setAllKeys(selectedRows, allValues),
    };
    setSelectedRows(nextSelected);
  };

  const onCloseActionBar = () => {
    const nextSelected = {
      ...setAllKeys(selectedRows, false),
    };
    setSelectedRows(nextSelected);
  };

  const handlePersonAdded = useCallback(
    (user: PapiSearchFieldPapiUserFragment) => {
      // Check if the permission set already has the user exclusivley by alias.
      // We do this to ensure there are no duplicates.
      if (!userPermissions.find((permissionObject) => permissionObject.user.alias.toLowerCase() === user.login.toLowerCase())) {
        addUserToPermissions({
          action: 'edit',
          permissionType: TrafficProjectPermissionType.TrafficProjectBindlePermission,
          user: {
            alias: user.login,
            firstName: user.firstName,
            lastName: user.lastName,
          },
        });

        onChangeRow(user.login)(false);
      }
    },
    [
      addUserToPermissions,
      onChangeRow,
      userPermissions,
    ],
  );
  return (
    <Table
      headerRows={1}
      rowComponents={[
        TableRow,
        UsersPermissionTableRow,
      ]}
      showDividers
      spacing="small"
    >
      <TableActionBar
        data-cy="users-permissions-table-action-bar"
        widths={[
          'fill',
        ]}
      >
        <PAPISearchField onSelectPerson={handlePersonAdded} />
        {numCheckedRows > 0 ? (
          <TableActionBarOverlay
            closeLabel="Cancel"
            label={
              <span>
                <strong>{numCheckedRows}</strong> {`user${numCheckedRows === 1 ? '' : 's'} `}
                selected
              </span>
            }
            onClose={onCloseActionBar}
          >
            <Button
              data-cy="users-permissions-table-action-bar-remove-button"
              onClick={() => removeUsersFromPermissionsByAlias(getSelectedAliases())}
              type="link"
            >
              Remove
            </Button>
          </TableActionBarOverlay>
        ) : null}
      </TableActionBar>
      <TableRow>
        {/* We don't extract the header to it own component because Meridian 7 loses the bold styling of column text */}
        <TableCell alignmentVertical="center">
          <Checkbox checked={allSelected && numCheckedRows > 0} disabled={removableRows.length === 0} onChange={onChangeHeader} />
        </TableCell>
        <TableCell>Name</TableCell>
        <TableCell>Alias</TableCell>
        <TableCell>Permission</TableCell>
        <TableCell />
      </TableRow>
      {userPermissions.map((userPermissionData) => (
        <UsersPermissionTableRow
          isRemovable={isRowRemovable(userPermissionData, userData, projectData)}
          key={userPermissionData.user.alias}
          onChangeRow={onChangeRow(userPermissionData.user.alias)}
          onRemove={(userPermission: UsersTableUserFragment) =>
            removeUsersFromPermissionsByAlias([
              userPermission.alias,
            ])
          }
          permissionData={userPermissionData}
          projectData={projectData}
          selected={(alias: string) => selectedRows[alias]}
          userData={userPermissionData.user}
        />
      ))}
    </Table>
  );
};
